import React, { FC, MouseEvent, useEffect, useMemo, useState } from 'react';
import { IDocumentData } from '@models/document/IDocumentData';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { UseFormReturn, useFormState, useWatch } from 'react-hook-form';
import { IFieldElem } from '@models/IFormData';
import { FormulaManager } from '@utils/managers/FormulaManager';
import clsx from 'clsx';
import hash from 'object-hash';
import { ITab } from '@/types';
import { handlerFieldWatch } from '@utils/fieldWatchUtils';

export interface IDocumentTab extends ITab {
    visibilityRules?: string;
    formMethods: UseFormReturn<IDocumentData>;
    fields: Record<string, IFieldElem>;
    includedFields: number[];
}

export interface IDocumentTabLabel {
    tab: IDocumentTab;
    index: number;
    selected: number;
    lastVisibleIndex: number;
    size: 'sm' | 'md';
    refTab: React.Ref<HTMLDivElement>;
    mouseHandler?: (e: MouseEvent) => void;
    visibilityEvent?: (index: number, visibilityStatus: boolean) => void;
}

const DocumentTabLabel: FC<IDocumentTabLabel> = ({
    tab,
    index,
    selected,
    lastVisibleIndex,
    size,
    refTab,
    mouseHandler,
    visibilityEvent,
}: IDocumentTabLabel) => {
    const document = useTypedSelector((state) => state.document);

    const visibilityRules = tab?.visibilityRules;
    const visibilityMng = useMemo(() => {
        const manager = new FormulaManager(visibilityRules!);
        manager.Init(tab.fields);
        return manager;
    }, [visibilityRules, tab.fields]);

    const [visibility, setVisibility] = useState<boolean>(false);
    const activated = React.useRef(false);

    const InitFormulas = async () => {
        let result = await visibilityMng.EvalFormulaValues(document.isEdit, document.isNew);
        if (activated.current) {
            setVisibility(result);
            //visibilityEvent && visibilityEvent(index, result);
        }
    };

    useEffect(() => {
        activated.current = true;
        InitFormulas();

        return () => {
            activated.current = false;
        };
    }, []);

    // Отслеживание изменений для видимости
    const watchVisibility = useWatch({ name: visibilityMng?.GetWatchFields() || [] });

    // Обработка изменений видимости
    useEffect(() => {
        if (!visibilityMng) return;

        const handleVisibilityChange = async () => {
            try {
                const result = await handlerFieldWatch(
                    watchVisibility,
                    document.isEdit,
                    document.isNew,
                    visibilityMng,
                    setVisibility,
                    activated,
                );

                if (activated.current) {
                    visibilityEvent?.(index, result);
                }
            } catch (error) {
                console.error('Error handling visibility change:', error);
            }
        };

        handleVisibilityChange();
    }, [watchVisibility, document.isEdit, document.isNew, visibilityMng, index, visibilityEvent]);

    // -------------------------------------------------------------------------------------------------------------------
    // Расчет валидности таба
    // -------------------------------------------------------------------------------------------------------------------
    const { errors, isValid: formIsValid } = useFormState({ control: tab.formMethods.control });

    const checkTabValidity = () => {
        // Если не режим редактирования/создания - всегда валидный
        if (!document.isEdit && !document.isNew) {
            return true;
        }

        // Если вкладка скрыта
        if (!visibility) {
            return true;
        }

        // Если нет ошибок в форме
        if (!errors?.fields || formIsValid) {
            return true;
        }

        // Проверяем, есть ли ошибки в полях этого таба
        const errorFieldIds = Object.keys(errors.fields).map((id) => parseInt(id, 10));
        return !errorFieldIds.some((fieldId) => tab.includedFields.includes(fieldId));
    };

    // хэш массива ошибок валидации формы
    const errorsFieldsHash = hash(errors?.fields || {});

    // Мемоизируем результат валидации для избежания лишних перерендеров
    const isValid = useMemo(() => {
        return checkTabValidity();
    }, [errorsFieldsHash]);
    // -------------------------------------------------------------------------------------------------------------------

    const buttonClasses = clsx('rf-tabs__button', `rf-tabs__button--${size}`, {
        'rf-tabs__button--active': index === selected && isValid,
        'rf-tabs__button--invalid': !isValid,
    });

    // Если таб не видим, не рендерим его
    if (!visibility) {
        return null;
    }

    return (
        <div
            key={index}
            className={clsx('rf-tabs__link', index > lastVisibleIndex && 'rf-tabs__link--hidden')}
            ref={refTab}
        >
            <button
                type="button"
                className={buttonClasses}
                disabled={tab.disabled}
                onClick={mouseHandler}
                title={!isValid ? 'В этом разделе есть ошибки валидации' : undefined}
            >
                {tab.icon && <div className="rf-tabs__icon">{tab.icon}</div>}
                {tab.label && <div>{tab.label}</div>}
            </button>
        </div>
    );
};

export default React.memo(DocumentTabLabel);
